/**
 * Espace personnel partenaire — Bénéficiaire Royalties.
 *
 * Un partenaire (rôle `partenaire`) accède UNIQUEMENT à cette page :
 * pas de sidebar, pas de menu, pas d'autre route accessible. Toutes
 * les données présentées ici sont scopées côté serveur via
 * `user.partner_beneficiary_id` (endpoints /api/partner/me/*).
 */
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  User2, Sparkles, Tag, Handshake, Percent, ArrowUpRight, ArrowDownRight,
  Minus, TrendingUp, Building2, Download, Eye, FileText, Calendar, CalendarDays,
  Fingerprint, Mail, Wallet, ClipboardList, PieChart, Ticket, ChevronRight,
  Vault, ArrowLeft, Award,
} from "lucide-react";

import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import StickyPageHeader from "../components/StickyPageHeader";
import PartnerCodeOfferDetails from "../components/PartnerCodeOfferDetails";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Skeleton } from "../components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";

// ─── Utilitaires ─────────────────────────────────────────────────────────
const fmtMoney = (n) =>
  new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(
    Number.isFinite(Number(n)) ? Number(n) : 0
  );
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-FR") : "—");
const fmtMonth = (m) => {
  if (!m) return "—";
  const [y, mo] = m.split("-");
  return new Date(Number(y), Number(mo) - 1, 1).toLocaleDateString("fr-FR", {
    month: "long", year: "numeric",
  });
};

const STATUS_LABEL = {
  active: { label: "Actif", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  upcoming: { label: "À venir", cls: "bg-sky-100 text-sky-800 border-sky-200" },
  expired: { label: "Expiré", cls: "bg-zinc-200 text-zinc-700 border-zinc-300" },
  disabled: { label: "Désactivé", cls: "bg-red-100 text-red-800 border-red-200" },
};

// Couleurs par période (façon Dashboard collaborateur)
const PERIOD_META = {
  day:   { label: "Aujourd'hui",   icon: CalendarDays, tint: "#0055A4" },
  week:  { label: "Cette semaine", icon: Calendar,     tint: "#0EA5E9" },
  month: { label: "Ce mois",       icon: PieChart,     tint: "#9333EA" },
  year:  { label: "Cette année",   icon: TrendingUp,   tint: "#EA580C" },
};

// ─── Sous-composants ─────────────────────────────────────────────────────
function ProgressionBadge({ pct, testId }) {
  if (pct === null || pct === undefined) {
    return (
      <span
        className="inline-flex items-center gap-1 text-[10px] text-zinc-500 font-mono uppercase tracking-wider"
        data-testid={testId}
      >
        <Minus size={11} /> N/A
      </span>
    );
  }
  const up = pct >= 0;
  const cls = up ? "text-emerald-700 bg-emerald-50 border-emerald-200" : "text-red-700 bg-red-50 border-red-200";
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded-sm border uppercase tracking-wider ${cls}`}
      data-testid={testId}
    >
      <Icon size={11} />
      {up ? "+" : ""}
      {pct}%
    </span>
  );
}

function KpiCard({ block, periodKey }) {
  const meta = PERIOD_META[periodKey];
  const Icon = meta.icon;
  return (
    <div
      className="bg-white border border-zinc-200 p-5 rounded-sm hover:border-zinc-300 transition-colors"
      data-testid={`partner-kpi-${periodKey}`}
    >
      <div className="flex items-start justify-between mb-4">
        <p className="label-uppercase">{meta.label}</p>
        <span
          className="h-9 w-9 inline-flex items-center justify-center rounded-sm"
          style={{ backgroundColor: `${meta.tint}14`, color: meta.tint }}
        >
          <Icon size={18} strokeWidth={1.75} />
        </span>
      </div>
      <p className="font-display text-3xl font-bold tracking-tight text-zinc-900 tabular-nums">
        {fmtMoney(block?.ca_net)}
      </p>
      <p className="text-xs text-zinc-500 mt-2 font-mono">
        CA net · {block?.tx_count || 0} tx
      </p>
      <div className="mt-3 pt-3 border-t border-zinc-100 flex items-center justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-zinc-400 font-mono">Royalties HT</p>
          <p className="text-lg font-display font-semibold text-purple-700 tabular-nums leading-tight">
            {fmtMoney(block?.royalties_ht)}
          </p>
        </div>
        <ProgressionBadge
          pct={block?.progression_pct_royalties}
          testId={`partner-progression-${periodKey}`}
        />
      </div>
    </div>
  );
}

const CODE_KIND_META = {
  promo:     { label: "Codes promotionnels", icon: Tag,       tint: "#EF4135" },
  affiliate: { label: "Codes d'affiliation", icon: Percent,   tint: "#0055A4" },
  referral:  { label: "Codes de parrainage", icon: Handshake, tint: "#9333EA" },
};

function CodeRow({ code }) {
  const st = STATUS_LABEL[code.status] || STATUS_LABEL.disabled;
  const meta = CODE_KIND_META[code.kind] || CODE_KIND_META.promo;
  // Détails de remise/offres uniquement pour promo & parrainage (l'affiliation
  // utilise commission_type/service_commissions — hors périmètre de la demande).
  const showOffers = code.kind === "promo" || code.kind === "referral";
  return (
    <div
      className="border border-zinc-200 rounded-sm p-3 space-y-2 hover:border-zinc-300 hover:shadow-sm transition-all bg-white"
      data-testid={`partner-code-${code.kind}-${code.id}`}
    >
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="flex items-start gap-2 min-w-0">
          <span
            className="h-8 w-8 inline-flex items-center justify-center rounded-sm shrink-0"
            style={{ backgroundColor: `${meta.tint}14`, color: meta.tint }}
          >
            <meta.icon size={14} strokeWidth={1.75} />
          </span>
          <div className="min-w-0">
            <p className="font-mono font-semibold text-zinc-900 truncate">{code.code}</p>
            {code.label ? (
              <p className="text-xs text-zinc-600 mt-0.5 truncate">{code.label}</p>
            ) : null}
            {code.description ? (
              <p className="text-[11px] text-zinc-500 mt-0.5 line-clamp-2" title={code.description}>
                {code.description}
              </p>
            ) : null}
          </div>
        </div>
        <Badge className={`${st.cls} border font-mono text-[10px] uppercase tracking-wider`}>
          {st.label}
        </Badge>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
        <div>
          <p className="text-[9px] uppercase tracking-wider text-zinc-400 font-mono">Début</p>
          <p className="font-mono">{fmtDate(code.start_date)}</p>
        </div>
        <div>
          <p className="text-[9px] uppercase tracking-wider text-zinc-400 font-mono">Fin</p>
          <p className="font-mono">{code.is_indefinite ? "∞" : fmtDate(code.end_date)}</p>
        </div>
        <div>
          <p className="text-[9px] uppercase tracking-wider text-zinc-400 font-mono">Utilisations</p>
          <p className="font-mono tabular-nums font-semibold text-zinc-900">
            {code.stats?.usage_count ?? 0}
          </p>
        </div>
        <div>
          <p className="text-[9px] uppercase tracking-wider text-zinc-400 font-mono">CA net</p>
          <p className="font-mono tabular-nums font-semibold text-emerald-700">
            {fmtMoney(code.stats?.ca_net)}
          </p>
        </div>
      </div>
      {showOffers ? (
        <PartnerCodeOfferDetails
          discountType={code.discount_type}
          discountValue={code.discount_value}
          offers={code.offers}
          accent={meta.tint}
          testIdPrefix={`partner-code-${code.kind}-${code.id}`}
        />
      ) : null}
      {code.stats?.services?.length ? (
        <div className="pt-2 border-t border-zinc-100">
          <p className="text-[9px] uppercase tracking-wider text-zinc-400 font-mono mb-1 flex items-center gap-1">
            <Ticket size={9} /> Services / produits associés (top 10)
          </p>
          <div className="flex flex-wrap gap-1">
            {code.stats.services.map((s) => (
              <span
                key={s.id}
                className="text-[11px] font-mono bg-zinc-50 border border-zinc-200 rounded-sm px-1.5 py-0.5"
                title={`${s.count} tx · ${fmtMoney(s.net)}`}
              >
                {s.name} · {s.count}
              </span>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function ProfileTile({ icon: Icon, label, value, tint = "#0055A4", testid, children }) {
  return (
    <div
      className="flex items-start gap-3 bg-white/70 backdrop-blur border border-white rounded-sm p-3"
      data-testid={testid}
    >
      <span
        className="h-9 w-9 inline-flex items-center justify-center rounded-sm shrink-0"
        style={{ backgroundColor: `${tint}18`, color: tint }}
      >
        <Icon size={16} strokeWidth={1.75} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">{label}</p>
        {value !== undefined ? (
          <p className="text-sm font-semibold text-zinc-900 mt-0.5 truncate">{value}</p>
        ) : null}
        {children}
      </div>
    </div>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────
export default function EspacePartenaire() {
  const { bid: bidParam } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  // Mode consultation admin/manager : l'URL contient un :bid ET l'utilisateur
  // n'est PAS un partenaire. Un partenaire sur cette route est renvoyé à son
  // espace personnel (safety net — la route est déjà protégée par RoleGuard
  // côté App.js).
  const adminConsult = Boolean(bidParam) && user?.role !== "partenaire";

  const [profile, setProfile] = useState(null);
  const [codes, setCodes] = useState(null);
  const [kpis, setKpis] = useState(null);
  const [statements, setStatements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(null);
  const [preview, setPreview] = useState(null); // { url, month }

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const params = adminConsult ? { bid: bidParam } : {};
        const [me, cds, kp, st] = await Promise.all([
          api.get("/partner/me", { params }),
          api.get("/partner/me/codes", { params }),
          api.get("/partner/me/kpis", { params }),
          api.get("/partner/me/statements", { params }),
        ]);
        if (!alive) return;
        setProfile(me.data);
        setCodes(cds.data);
        setKpis(kp.data);
        setStatements(st.data);
      } catch (e) {
        console.error(e);
        toast.error("Erreur de chargement du tableau de bord");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [adminConsult, bidParam]);

  const totalActiveCodes = useMemo(() => {
    if (!codes) return 0;
    return ["promo", "affiliate", "referral"].reduce(
      (s, k) => s + (codes[k] || []).filter((c) => c.is_active).length, 0
    );
  }, [codes]);

  const handleDownload = async (bid, month) => {
    try {
      setDownloading(month);
      const res = await api.get(`/royalty-statements/${bid}/${month}/pdf`, {
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `releve_royalties_${month}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      toast.error("Impossible de télécharger le relevé");
    } finally {
      setDownloading(null);
    }
  };

  const handlePreview = async (bid, month) => {
    try {
      setPreviewLoading(month);
      const res = await api.get(`/royalty-statements/${bid}/${month}/pdf`, {
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      setPreview({ url, month });
    } catch (e) {
      console.error(e);
      toast.error("Impossible d'ouvrir le relevé");
    } finally {
      setPreviewLoading(null);
    }
  };

  const closePreview = () => {
    if (preview?.url) URL.revokeObjectURL(preview.url);
    setPreview(null);
  };

  useEffect(() => {
    return () => {
      if (preview?.url) URL.revokeObjectURL(preview.url);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <div className="space-y-6" data-testid="partner-loading">
        <Skeleton className="h-40 w-full" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-44" />)}
        </div>
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="partner-dashboard">
      {/* ═══════════ Bandeau mode consultation admin/manager ═══════════ */}
      {adminConsult && (
        <div
          className="flex items-center justify-between gap-3 flex-wrap border border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50 px-4 py-3 rounded-sm shadow-sm"
          data-testid="partner-admin-consult-banner"
        >
          <div className="flex items-center gap-3 min-w-0">
            <span className="h-9 w-9 inline-flex items-center justify-center rounded-sm bg-amber-600 text-white shrink-0">
              <Eye size={16} strokeWidth={1.8} />
            </span>
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-[0.18em] text-amber-800 font-mono leading-none mb-1">
                Mode consultation à distance
              </p>
              <p className="text-sm font-semibold text-amber-950 truncate">
                Vous consultez l'espace personnel de <span className="font-mono">{profile?.name || "…"}</span>
                {profile?.partner_id ? (
                  <span className="ml-1 text-xs font-mono text-amber-700">· {profile.partner_id}</span>
                ) : null}
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate("/kpi-transactions?tab=royalties")}
            data-testid="partner-admin-consult-back"
            className="border-amber-300 text-amber-800 hover:bg-amber-100 hover:text-amber-900 shrink-0"
          >
            <ArrowLeft size={13} className="mr-1.5" />
            Retour à la liste
          </Button>
        </div>
      )}

      {/* ═══════════ En-tête de page ═══════════ */}
      <StickyPageHeader testid="partner-sticky-header">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="label-uppercase mb-2">Espace personnel — Tableau de bord</p>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-display font-bold tracking-tight text-zinc-900 flex items-center gap-3">
              <Sparkles size={28} strokeWidth={1.5} className="text-purple-600" />
              Bonjour {profile?.name || "Partenaire"}
            </h1>
            <p className="text-sm text-zinc-600 mt-1 font-mono">
              Suivi temps réel de votre activité · {totalActiveCodes} code(s) actif(s) · Taux Royalties {profile?.rate}%
            </p>
          </div>
          <div className="flex flex-col items-center gap-2 sm:flex sm:flex-row sm:items-center sm:gap-4 sm:flex-wrap sm:justify-end w-full sm:w-auto">
            <div className="text-center sm:text-right">
              <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono leading-tight">
                Cumul annuel Royalties
              </p>
              <p className="font-display text-base sm:text-2xl font-bold text-purple-700 tabular-nums whitespace-nowrap" data-testid="partner-year-total">
                {fmtMoney(kpis?.year?.royalties_ht)}
              </p>
            </div>
            <div className="h-10 w-px bg-zinc-200 hidden sm:block" />
            <div className="text-center sm:text-right">
              <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono leading-tight">
                Cumul Royalties versés
              </p>
              <p className="font-display text-base sm:text-2xl font-bold text-emerald-700 tabular-nums whitespace-nowrap" data-testid="partner-year-paid">
                {fmtMoney(kpis?.payment_summary?.paid_ht)}
              </p>
            </div>
            <div className="h-10 w-px bg-zinc-200 hidden sm:block" />
            <div className="text-center sm:text-right">
              <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono leading-tight">
                Cumul Royalties à percevoir
              </p>
              <p className="font-display text-base sm:text-2xl font-bold text-amber-700 tabular-nums whitespace-nowrap" data-testid="partner-year-pending">
                {fmtMoney(kpis?.payment_summary?.pending_ht)}
              </p>
            </div>
          </div>
        </div>
      </StickyPageHeader>

      {/* ═══════════ Section 1 : Mes informations ═══════════ */}
      <Card
        className="border-purple-200 bg-gradient-to-br from-purple-50 via-white to-fuchsia-50 overflow-hidden relative"
        data-testid="partner-profile"
      >
        <div
          className="absolute -top-16 -right-16 h-40 w-40 rounded-full bg-gradient-to-br from-purple-200 to-fuchsia-200 opacity-40 blur-2xl"
          aria-hidden
        />
        <CardHeader className="pb-3 relative">
          <div className="flex items-center gap-2">
            <span className="h-9 w-9 inline-flex items-center justify-center rounded-sm bg-purple-600 text-white">
              <User2 size={16} strokeWidth={1.75} />
            </span>
            <div>
              <CardTitle className="text-base font-display">Mes informations</CardTitle>
              <p className="text-xs text-zinc-500 mt-0.5">
                Identité & conditions du contrat partenaire
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 relative">
          <ProfileTile
            icon={User2}
            label="Partenaire"
            tint="#9333EA"
            testid="partner-tile-name"
          >
            <p className="text-sm font-semibold text-zinc-900 mt-0.5" data-testid="partner-name">
              {profile?.name || "—"}
            </p>
            <p className="text-xs text-zinc-500 font-mono mt-0.5 truncate flex items-center gap-1">
              <Mail size={10} /> {profile?.email}
            </p>
          </ProfileTile>
          <ProfileTile
            icon={Fingerprint}
            label="ID Partenaire"
            tint="#0EA5E9"
            testid="partner-tile-id"
          >
            <p
              className="text-sm font-mono font-bold text-sky-700 mt-0.5 tracking-wider"
              data-testid="partner-id"
            >
              {profile?.partner_id || "—"}
            </p>
          </ProfileTile>
          <ProfileTile
            icon={Award}
            label="Catégorie partenariat"
            tint="#7C3AED"
            testid="partner-tile-category"
          >
            <p
              className="text-sm font-semibold text-violet-700 mt-0.5"
              data-testid="partner-category"
            >
              {profile?.partnership_category === "ambassadeur"
                ? "Ambassadeur(rice) de Marque"
                : profile?.partnership_category === "autre"
                ? "Autre partenariat"
                : "—"}
            </p>
          </ProfileTile>
          <ProfileTile
            icon={Percent}
            label="Taux Royalties"
            tint="#EF4135"
            testid="partner-tile-rate"
          >
            <p
              className="font-display text-2xl font-bold text-red-600 tabular-nums mt-0.5 leading-tight"
              data-testid="partner-rate"
            >
              {profile?.rate}%
            </p>
          </ProfileTile>
          <ProfileTile
            icon={CalendarDays}
            label="Période contractuelle"
            tint="#EA580C"
            testid="partner-tile-contract"
          >
            <p className="font-mono text-xs text-zinc-900 mt-0.5">
              {fmtDate(profile?.contract_start)}
            </p>
            <p className="font-mono text-xs text-zinc-500 flex items-center gap-1 mt-0.5">
              <ChevronRight size={10} />
              {profile?.contract_end ? fmtDate(profile?.contract_end) : "∞ (indéterminée)"}
            </p>
          </ProfileTile>
          <ProfileTile
            icon={Building2}
            label="Départements associés"
            tint="#0055A4"
            testid="partner-tile-brands"
          >
            <div className="flex flex-wrap gap-1 mt-1" data-testid="partner-brands">
              {(profile?.brands || []).length === 0 ? (
                <span className="text-xs text-zinc-500">—</span>
              ) : (
                profile.brands.map((b) => (
                  <span
                    key={b.id}
                    className="text-[10px] uppercase tracking-wider font-mono border rounded-sm px-1.5 py-0.5"
                    style={{
                      background: `${b.color || "#0055A4"}18`,
                      color: b.color || "#0055A4",
                      borderColor: `${b.color || "#0055A4"}40`,
                    }}
                  >
                    {b.name}
                  </span>
                ))
              )}
            </div>
          </ProfileTile>
          <ProfileTile
            icon={Wallet}
            label="Statut du partenariat"
            tint="#059669"
            testid="partner-tile-status"
          >
            <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 border font-mono text-[10px] uppercase tracking-wider mt-0.5">
              Actif
            </Badge>
          </ProfileTile>
        </CardContent>
      </Card>

      {/* ═══════════ KPI CA net + Royalties + Progression ═══════════ */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <span className="h-8 w-8 inline-flex items-center justify-center rounded-sm bg-gradient-to-br from-sky-500 to-blue-600 text-white">
            <TrendingUp size={14} strokeWidth={1.75} />
          </span>
          <div>
            <h2 className="text-sm font-display font-semibold text-zinc-900 uppercase tracking-wider">
              CA net · Royalties · Progression
            </h2>
            <p className="text-xs text-zinc-500 mt-0.5">
              Cumuls temps réel — évolution comparée à la période précédente
            </p>
          </div>
        </div>
        <div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3"
          data-testid="partner-kpis"
        >
          <KpiCard block={kpis?.day}   periodKey="day" />
          <KpiCard block={kpis?.week}  periodKey="week" />
          <KpiCard block={kpis?.month} periodKey="month" />
          <KpiCard block={kpis?.year}  periodKey="year" />
        </div>
        {/* Détail par département (mois en cours) */}
        {kpis?.month?.by_brand?.length ? (
          <Card className="mt-3 border-zinc-200" data-testid="partner-brand-breakdown">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <span className="h-7 w-7 inline-flex items-center justify-center rounded-sm bg-blue-50 text-blue-700">
                  <Building2 size={13} strokeWidth={1.75} />
                </span>
                <CardTitle className="text-xs uppercase tracking-wider font-mono text-zinc-700">
                  Détail par département · {fmtMonth(kpis.month.start?.slice(0, 7))}
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="divide-y divide-zinc-100">
                {kpis.month.by_brand.map((b) => (
                  <div
                    key={b.brand_id}
                    className="flex items-center justify-between py-2.5 text-sm gap-3 flex-wrap"
                    data-testid={`partner-brand-detail-${b.brand_id}`}
                  >
                    <span className="flex items-center gap-2 min-w-0">
                      <span
                        className="h-3 w-3 rounded-sm shrink-0"
                        style={{ background: b.color || "#9333EA" }}
                      />
                      <span className="font-medium truncate">{b.name}</span>
                    </span>
                    <span className="font-mono tabular-nums flex items-center gap-4">
                      <span className="text-zinc-700">
                        <span className="text-[10px] text-zinc-400 uppercase tracking-wider mr-1">CA net</span>
                        {fmtMoney(b.ca_net)}
                      </span>
                      <span className="text-purple-700 font-semibold">
                        <span className="text-[10px] text-purple-400 uppercase tracking-wider mr-1">Royalties</span>
                        {fmtMoney(b.royalties_ht)}
                      </span>
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ) : null}
      </div>

      {/* ═══════════ Codes (promo / affiliation / parrainage) ═══════════ */}
      <div>
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <span className="h-8 w-8 inline-flex items-center justify-center rounded-sm bg-gradient-to-br from-purple-500 to-fuchsia-600 text-white">
              <Sparkles size={14} strokeWidth={1.75} />
            </span>
            <div>
              <h2 className="text-sm font-display font-semibold text-zinc-900 uppercase tracking-wider">
                Mes codes
              </h2>
              <p className="text-xs text-zinc-500 mt-0.5">
                Statut & performances de vos codes rattachés
              </p>
            </div>
          </div>
          <span className="text-xs text-zinc-500 font-mono">
            <span className="inline-block h-2 w-2 rounded-full bg-emerald-500 mr-1.5 animate-pulse" />
            {totalActiveCodes} code(s) actif(s)
          </span>
        </div>
        <Tabs defaultValue="promo" data-testid="partner-codes-tabs">
          <TabsList className="grid grid-cols-3 w-full sm:w-auto">
            <TabsTrigger value="promo" data-testid="partner-tab-promo">
              <Tag size={12} className="mr-1 text-red-500" /> Promo ({codes?.promo?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="affiliate" data-testid="partner-tab-affiliate">
              <Percent size={12} className="mr-1 text-blue-500" /> Affiliation ({codes?.affiliate?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="referral" data-testid="partner-tab-referral">
              <Handshake size={12} className="mr-1 text-purple-500" /> Parrainage ({codes?.referral?.length || 0})
            </TabsTrigger>
          </TabsList>
          {["promo", "affiliate", "referral"].map((k) => (
            <TabsContent key={k} value={k} className="mt-3">
              {(codes?.[k] || []).length === 0 ? (
                <p className="text-sm text-zinc-500 italic p-6 text-center border border-dashed border-zinc-200 rounded-sm bg-zinc-50/40">
                  Aucun code {k === "promo" ? "promotionnel" : k === "affiliate" ? "d'affiliation" : "de parrainage"} rattaché.
                </p>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                  {codes[k].map((c) => <CodeRow key={c.id} code={c} />)}
                </div>
              )}
            </TabsContent>
          ))}
        </Tabs>
      </div>

      {/* ═══════════ Relevés Royalties validés ═══════════ */}
      <Card className="border-zinc-200" data-testid="partner-statements">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <span className="h-9 w-9 inline-flex items-center justify-center rounded-sm bg-gradient-to-br from-amber-500 to-orange-600 text-white">
              <FileText size={16} strokeWidth={1.75} />
            </span>
            <div>
              <CardTitle className="text-base font-display">Mes relevés Royalties</CardTitle>
              <p className="text-xs text-zinc-500 mt-0.5">
                Uniquement les relevés validés par un administrateur — téléchargeables au format PDF
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {statements.length === 0 ? (
            <div className="p-8 text-center border border-dashed border-zinc-200 rounded-sm bg-zinc-50/40">
              <ClipboardList size={28} className="mx-auto text-zinc-300 mb-2" strokeWidth={1.5} />
              <p className="text-sm text-zinc-500 italic">
                Aucun relevé validé pour l'instant.
              </p>
              <p className="text-xs text-zinc-400 mt-1">
                Vos relevés mensuels apparaîtront ici dès qu'ils seront validés par l'administrateur.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-zinc-100">
              {statements.map((s) => (
                <div
                  key={s.id}
                  className="flex items-center justify-between gap-3 py-3 flex-wrap"
                  data-testid={`partner-statement-${s.month}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="h-10 w-10 inline-flex items-center justify-center rounded-sm bg-amber-50 text-amber-700 border border-amber-200 shrink-0">
                      <FileText size={16} strokeWidth={1.5} />
                    </span>
                    <div className="min-w-0">
                      <p className="font-semibold text-zinc-900 capitalize truncate">{fmtMonth(s.month)}</p>
                      <p className="text-xs text-zinc-500 font-mono truncate">
                        Validé le {fmtDate(s.validated_at)} · CA net {fmtMoney(s.ca_net)} · Royalties {fmtMoney(s.amount_ht)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handlePreview(profile.beneficiary_id, s.month)}
                      disabled={previewLoading === s.month}
                      data-testid={`partner-statement-preview-${s.month}`}
                      className="border-zinc-300 text-zinc-700 hover:bg-zinc-50 hover:text-zinc-900"
                      title="Visualiser le relevé"
                    >
                      <Eye size={14} className="mr-1.5" />
                      {previewLoading === s.month ? "…" : "Visualiser"}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDownload(profile.beneficiary_id, s.month)}
                      disabled={downloading === s.month}
                      data-testid={`partner-statement-download-${s.month}`}
                      className="border-amber-200 text-amber-700 hover:bg-amber-50 hover:text-amber-800"
                    >
                      <Download size={14} className="mr-1.5" />
                      {downloading === s.month ? "…" : "Télécharger"}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ═══════════ Modal Prévisualisation PDF Relevé ═══════════ */}
      <Dialog open={!!preview} onOpenChange={(o) => { if (!o) closePreview(); }}>
        <DialogContent
          className="max-w-5xl w-[95vw] h-[90vh] p-0 flex flex-col gap-0"
          data-testid="partner-statement-preview-modal"
        >
          <DialogHeader className="px-5 py-3 border-b border-zinc-200 flex-row items-center justify-between space-y-0">
            <DialogTitle className="text-base font-semibold text-zinc-900 capitalize flex items-center gap-2">
              <FileText size={16} className="text-amber-700" />
              Relevé Royalties · {preview ? fmtMonth(preview.month) : ""}
            </DialogTitle>
            <DialogDescription className="sr-only">
              Aperçu du relevé Royalties du mois sélectionné.
            </DialogDescription>
            <div className="flex items-center gap-2">
              {preview && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleDownload(profile.beneficiary_id, preview.month)}
                  disabled={downloading === preview.month}
                  className="border-amber-200 text-amber-700 hover:bg-amber-50 hover:text-amber-800"
                  data-testid="partner-statement-preview-download"
                >
                  <Download size={14} className="mr-1.5" />
                  Télécharger
                </Button>
              )}
            </div>
          </DialogHeader>
          <div className="flex-1 bg-zinc-100 min-h-0">
            {preview && (
              <iframe
                src={preview.url}
                title="Aperçu du relevé Royalties"
                className="w-full h-full border-0"
                data-testid="partner-statement-preview-iframe"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
